import React, { useState } from 'react';
import { Save, Plus, Trash2, Pencil } from 'lucide-react';
import { apiCall, useAdminData, ImageUploader } from './_helpers';

const blank = () => ({ image_url: '', caption: '', post_url: '', order: 0, is_active: true });

const SocialPostsManager = ({ onMessage }) => {
  const [items, , refetch] = useAdminData('social-posts');
  const [editing, setEditing] = useState(null);
  const isEdit = !!(editing && editing._id);

  const save = async () => {
    try {
      if (isEdit) {
        const { _id, ...payload } = editing;
        await apiCall('PUT', `social-posts/${_id}`, payload);
      } else {
        await apiCall('POST', 'social-posts', editing);
      }
      onMessage('success', 'Saved');
      setEditing(null);
      refetch();
    } catch (e) { onMessage('error', e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await apiCall('DELETE', `social-posts/${id}`); onMessage('success', 'Deleted'); refetch(); }
    catch (e) { onMessage('error', e.message); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Instagram Feed</h2>
          <p className="text-sm text-gray-500 mt-1">Shows 6 tiles between Gallery and Newsletter on the homepage. Upload images and link each to a real Instagram post URL.</p>
        </div>
        <button onClick={() => setEditing(blank())} className="bg-[#C9A86C] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#B8956A]" data-testid="add-social-btn">
          <Plus size={18} /> Add Post
        </button>
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded text-sm text-gray-700">
        <strong>Manual curation</strong> — this is not a live Instagram pull. Add a square image + the link to the actual
        Instagram post for each tile. Set 6 tiles to <em>Active</em> to make the section appear on the homepage.
      </div>

      {editing && (
        <div className="bg-white rounded-xl p-6 shadow-md mb-6 space-y-3" data-testid="social-form">
          <ImageUploader currentImage={editing.image_url} onUpload={(url) => setEditing({ ...editing, image_url: url })} label="Upload Square Image" />
          <input type="text" placeholder="Image URL (or upload above)" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="w-full border rounded-lg px-4 py-2" data-testid="social-image-input" />
          <input type="text" placeholder="Instagram Post URL" value={editing.post_url} onChange={(e) => setEditing({ ...editing, post_url: e.target.value })} className="w-full border rounded-lg px-4 py-2" />
          <input type="text" placeholder="Caption (for alt text)" value={editing.caption} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} className="w-full border rounded-lg px-4 py-2" />
          <input type="number" placeholder="Order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || '0') })} className="w-full border rounded-lg px-4 py-2" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active
          </label>
          <div className="flex gap-2">
            <button onClick={save} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><Save size={18} /> {isEdit ? 'Update' : 'Save'}</button>
            <button onClick={() => setEditing(null)} className="bg-gray-500 text-white px-4 py-2 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <div key={p._id} className="bg-white rounded-xl overflow-hidden shadow-md" data-testid={`social-card-${p._id}`}>
            <img src={p.image_url} alt={p.caption} className="w-full aspect-square object-cover bg-gray-100" />
            <div className="p-3 flex items-center gap-2 text-xs">
              <span className="text-gray-500">#{p.order || 0}</span>
              {p.is_active === false && <span className="text-gray-400 bg-gray-100 px-2 py-1 rounded">Hidden</span>}
              <button onClick={() => setEditing({ ...p })} className="ml-auto text-blue-600 hover:bg-blue-50 p-1 rounded" data-testid={`edit-social-${p._id}`}><Pencil size={14} /></button>
              <button onClick={() => remove(p._id)} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`delete-social-${p._id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="p-8 text-center text-gray-500">No posts yet.</p>}
    </div>
  );
};

export default SocialPostsManager;
